import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { finalize, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { FileSaverService } from 'ngx-filesaver';
import { Store } from '@ngrx/store';

import { ToasterService } from 'common/src/modules/ui-components/toaster';
import { BaseModalComponent } from 'common/src/modules/rnpl-common/components';
import { ReportingPeriodsApiService } from 'projects/workspace/src/app/reporting-periods/services/reporting-periods-api.service';
import {
  ReportingPeriodModel,
  ReportingPeriodsPreferencesModel, UnreportedDocumentsListTotalsModel
} from 'projects/workspace/src/app/reporting-periods/models';
import { CustomSearchFn, getContentDispositionFileName } from '../../../rnpl-common/helpers';
import { PartnersApiService } from 'projects/workspace/src/app/partners/corporate/services/partner-api.service';
import { selectCompanyProfile } from 'projects/workspace/src/app/administration/store/selectors';
import { CompanyProfile } from 'projects/workspace/src/app/administration/models/company-profile.model';
import { AppState } from 'projects/workspace/src/app/store/state/app.state';
import { FileService } from '../../../../services/file.service';
import { FileUploadParams } from '../../../../models/file-upload-params.model';



@Component({
  selector: 'rnpl-reporting-periods-report-period-modal',
  templateUrl: './reporting-periods-report-period-modal.component.html'
})
export class ReportingPeriodsReportPeriodModalComponent extends BaseModalComponent implements OnInit {

  public reportingPeriodPreferences: ReportingPeriodsPreferencesModel;
  public selectedPeriod: ReportingPeriodModel = null;
  public selectedTaxAdvisor: any = null;
  public hiddenTo: string = null;
  public sendTo: string = null;
  public isHiddenCopy = false;
  public wid: number;
  public emailEmptyValidation: boolean = false;
  public emailHiddenEmptyValidation: boolean = false;

  readonly reportingPeriods$: BehaviorSubject<ReportingPeriodModel[]> = new BehaviorSubject<ReportingPeriodModel[]>([]);
  readonly customers$: Observable<any[]>;
  readonly showDropdownSpin$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  readonly confirmRequest$: BehaviorSubject<boolean> = new BehaviorSubject(null);
  readonly downloadFileRequest$: BehaviorSubject<boolean> = new BehaviorSubject(null);
  private destroy$: ReplaySubject<any> = new ReplaySubject<any>(1);

  public customSearchFn = CustomSearchFn;

  constructor(
    public toasterService: ToasterService,
    private reportingPeriodsApiService: ReportingPeriodsApiService,
    private partnersApiService: PartnersApiService,
    private fileService: FileService,
    private fileSaverService: FileSaverService,
    public dialogRef: MatDialogRef<ReportingPeriodsReportPeriodModalComponent>,
    private readonly store: Store<AppState>,
    @Inject(MAT_DIALOG_DATA) public data: {
      reportingPeriod: ReportingPeriodModel,
      disablePeriod: boolean
    }
  ) {
    super(toasterService);

    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.dialogRef.close();
      }
    });

    this.customers$ = this.partnersApiService.getCombinedPartnersList();

    this.store.select(selectCompanyProfile)
      .pipe(takeUntil(this.destroy$))
      .subscribe((profile: CompanyProfile) => {
        this.wid = profile.workspaceId;
        this.reportingPeriodsApiService.getReportingPeriodsPreferences(this.wid)
          .pipe(takeUntil(this.destroy$))
          .subscribe(preferences => {
            this.reportingPeriodPreferences = preferences;
            this.selectedTaxAdvisor = preferences.taxAdvisor;
            this.sendTo = preferences.taxAdvisorEmailTo;
            this.setActiveReportingPeriod();
          });
      });

    this.reportingPeriodsApiService
      .getReportingPeriodsListByStatuses(['OPEN', 'PENDING', 'PARTIALLY_REPORTED', 'UNREPORTED', 'OVERDUE'], 'period.view')
      .pipe(takeUntil(this.destroy$))
      .subscribe((periods: ReportingPeriodModel[]) => {
        const list: ReportingPeriodModel[] = periods.map((p: ReportingPeriodModel) => ({
          ...p,
          label: p.period.view
        }));

        this.reportingPeriods$.next(list);
        this.setActiveReportingPeriod();

        this.reportingPeriodsApiService.getUnreportedDocumentsCounter()
          .pipe(takeUntil(this.destroy$))
          .subscribe((count: number) => {
            if (!!count) {
              // add last option for unreported documents
              list.push({
                label: 'REPORTING_PERIODS.UNREPORTED_DOCUMENTS',
                unreportedOption: true
              } as any);

              this.reportingPeriods$.next([...list]);
            }
          });
      });
  }

  ngOnInit(): void {
    if (this.data && this.data.reportingPeriod) {
      this.selectedPeriod = {
        ...this.data.reportingPeriod,
        label: this.data.reportingPeriod.period.view
      };
      this.selectedTaxAdvisor = this.data.reportingPeriod.advisor;
      this.sendTo = this.data.reportingPeriod.advisorEmail;
    }
  }

  public periodChanged(): void {
    if (this.selectedPeriod && this.selectedPeriod.advisor) {
      this.selectedTaxAdvisor = this.selectedPeriod.advisor;
    } else if (this.selectedPeriod.unreportedOption) {
      this.selectedTaxAdvisor = this.reportingPeriodPreferences.taxAdvisor;

      this.reportingPeriodsApiService.getUnreportedDocumentsListTotals(this.wid)
        .pipe(takeUntil(this.destroy$))
        .subscribe((data: UnreportedDocumentsListTotalsModel) => {
          this.selectedPeriod = {
            ...this.selectedPeriod,
            oinCount: data.oinCount,
            iinCount: data.iinCount,
            ocnCount: data.ocnCount,
            icnCount: data.icnCount,
            pmntCount: data.pmntCount,
          };
        });
    }
  }

  public setActiveReportingPeriod(): void {
    const periods = this.reportingPeriods$.getValue();
    if (this.selectedPeriod || !periods.length || !this.reportingPeriodPreferences) {
      return;
    }

    const today = new Date();
    const activePeriods = periods
      .filter(period => !period.unreportedOption)
      .filter(period => period.period.type === this.reportingPeriodPreferences.period.period)
      .filter(period => (new Date(period.period.firstDay) < today) && (new Date(period.period.lastDay) > today));

    this.selectedPeriod = activePeriods.length
      ? activePeriods[0]
      : null;
  }

  public onDownloadReportPeriod(): void {
    if (!this.selectedPeriod) { return; }
    if (this.downloadFileRequest$.getValue()) { return; }
    this.toasterService.notifyRequestMessage(
      {key: 'generateReportingPeriod', message: 'REPORTING_PERIODS.REPORT_IS_EXPORTING'}
    );
    this.downloadFileRequest$.next(true);

    const request$ = this.selectedPeriod.unreportedOption
      ? this.reportingPeriodsApiService.getDownloadReportingUnreportedPeriodsExportParams(this.wid)
      : this.reportingPeriodsApiService.getDownloadReportingPeriodsExportParams(this.wid);

    request$
      .pipe(takeUntil(this.destroy$))
      .subscribe((fileParams: FileUploadParams) => {
        this.downloadFile(fileParams.url, [this.selectedPeriod.id]);
      });
  }

  downloadFile(url, ids: number[]) {
    const request$ = this.selectedPeriod.unreportedOption
      ? this.fileService.downloadFilePostRequest(url, {})
      : this.fileService.downloadFilePostRequest(url, {ids});

    request$
      .pipe(
        finalize(() => {
          this.toasterService.hideRequestMessage('generateReportingPeriod');
          this.downloadFileRequest$.next(false);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((res: any) => {
        this.fileSaverService.save(
          res.body,
          getContentDispositionFileName(res.headers.get('Content-Disposition'))
        );
      }, (error) => {
        // backend messages are already toasted by FileService.handleErrors
        if (!error.error || !error.error.errors || !error.error.errors.length) {
          this.displayMessage('error', error.message);
        }
      });
  }

  public onReportPeriod(): void {
    if (!this.sendTo || (this.isHiddenCopy && !this.hiddenTo)) {
      if (!this.sendTo) {
        this.emailEmptyValidation = true;
      }
      if (this.isHiddenCopy && !this.hiddenTo) {
        this.emailHiddenEmptyValidation = true;
      }
      return;
    }

    const sendTo = this.sendTo
      ? this.sendTo.split(',')
      : [];
    const hiddenTo = this.hiddenTo
      ? this.hiddenTo.split(',')
      : [];
    const reportId = this.selectedPeriod
      ? this.selectedPeriod.id
      : null;

    if (this.confirmRequest$.getValue()) { return; }
    this.confirmRequest$.next(true);

    const request$ = this.selectedPeriod.unreportedOption
      ? this.reportingPeriodsApiService.reportUnreportedPeriods(this.wid, this.selectedTaxAdvisor, sendTo, hiddenTo)
      : this.reportingPeriodsApiService.reportSinglePeriod(this.wid, this.selectedTaxAdvisor, reportId, sendTo, hiddenTo);

    request$
      .pipe(
        finalize(() => this.confirmRequest$.next(false)),
        takeUntil(this.destroy$)
      )
      .subscribe(res => {
        if (res) {
          this.dialogRef.close(res);
        }
      });
  }

}
